import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { LibraryModule } from '../shared/lib/lib.module';
import { NiTableComponent } from '../shared/lib/ni-table/ni-table.component';
import { NiTableCellDirective } from '../shared/lib/ni-table/ni-table-cell.directive';
import { NiTableColumn } from '../shared/lib/ni-table/ni-table.models';
import { StaffLocationsTooltipDirective } from './staff-locations-tooltip.directive';
import { StaffAdminService } from '../core/services/staff-admin.service';
import { StaffListItem } from '../core/models/staff.model';

@Component({
  selector: 'staff-management-component, StaffManagementComponent',
  standalone: true,
  templateUrl: './staff-management.component.html',
  styleUrls: ['./staff-management.component.css'],
  imports: [CommonModule, FormsModule, LibraryModule, RouterModule, NiTableComponent, NiTableCellDirective, StaffLocationsTooltipDirective],
})
export class StaffManagementComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly staffAdminService = inject(StaffAdminService);
  private readonly searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  isInitialLoading = true;
  isTableLoading = false;
  errorMessage: string | null = null;
  staffList: StaffListItem[] = [];
  columns: NiTableColumn<StaffListItem>[] = [];
  searchText = '';
  currentPage = 1;
  pageSize = 5;
  total = 0;

  ngOnInit(): void {
    this.columns = [
      { key: 'staffName', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'sfId', label: 'SF ID' },
      {
        key: 'status',
        label: 'Status',
        formatter: (_, row) => (row.status ? 'Active' : 'Inactive'),
      },
      {
        key: 'locations',
        label: 'Locations',
        custom: true,
      },
      {
        key: 'edit',
        label: 'Edit',
        actionLabel: 'Edit',
        onAction: (row) => this.onEditStaff(row),
      },
    ];

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 1;
        this.fetchStaff();
      });

    this.fetchStaff(true);
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get showPagination(): boolean {
    return this.total > this.pageSize;
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value.trim());
  }

  goBack(): void {
    void this.router.navigate(['/programs/senior-center']);
  }

  onCreateStaff(): void {
    void this.router.navigate(['/staff-management/create']);
  }

  onManageLocations(): void {
    void this.router.navigate(['/location-management']);
  }

  onEditStaff(row: StaffListItem): void {
    void this.router.navigate(['/staff-management', row.id, 'edit']);
  }

  onPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchStaff();
    }
  }

  onNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchStaff();
    }
  }

  getLocationsText(row: StaffListItem): string {
    const names = (row.locations ?? [])
      .map((location) => location.locationName)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  }

  private fetchStaff(isInitial = false): void {
    if (isInitial) {
      this.isInitialLoading = true;
    } else {
      this.isTableLoading = true;
    }
    this.errorMessage = null;

    this.staffAdminService
      .listStaff({
        index: this.currentPage - 1,
        limit: this.pageSize,
        searchText: this.searchText.trim(),
      })
      .subscribe({
        next: (response) => {
          this.staffList = response.staff ?? [];
          this.total = response.total ?? 0;
          this.isInitialLoading = false;
          this.isTableLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message ?? 'Unable to load staff members.';
          this.isInitialLoading = false;
          this.isTableLoading = false;
        },
      });
  }
}
